import classNames from "classnames";
import { NextSeo } from "next-seo";
import React, { useRef, useState } from "react";

import { getTitleDescription } from "@/utils/nextjs/head";
import { getSEONoFollow, getSEONoIndex } from "@/utils/nextjs/seo";
import { path } from "@/utils/path";
import { TYPE_GABARIT } from "@/utils/enums";

import useMediaImage from "@/hooks/useMediaImage";
import { useItemView } from "@/hooks/analytics";

import Layout from "@/layout";

import TqVideoPlayer from "@/tq/TqVideoPlayer";

import Breadcrumb from "@/ui/breadcrumb";
import Cartes from "@/ui/cartes";
import Image from "@/ui/image";
import Date from "@/ui/date";
import VodDisponible from "@/ui/vodDisponible";
import BoutonsPartage from "@/ui/boutonsPartage";
import PlayIconInTo from "@/ui/playIconInTo";

import styles from "./episode.module.scss";

import { Episode } from "@/types/emissions";
import { Media } from "@/types/contenu";

type PropTypes = {
    episode: Episode;
};

const EpisodePage = ({ episode }: PropTypes) => {
    const {
        dateDiffusion,
        description,
        emission,
        noEpisode,
        noSaison,
        recettes,
        titre,
        type,
    } = episode || {};

    const emissionTitre = emission?.titre || emission?.nom;
    const title =
        noSaison && noEpisode
            ? `Saison ${noSaison} Épisode ${noEpisode} - ${emissionTitre}`
            : emissionTitre;

    const pageRef = useRef<HTMLDivElement>(null);

    const [isVideoStarted, setIsVideoStarted] = useState(false);

    const mediaImage = useMediaImage(emission?.images);

    useItemView(episode);

    const handlePlayerEvent = (eventName: string, args: string) => {
        switch (eventName) {
            case "playStateChanged":
                if (pageRef && pageRef.current) {
                    if (args === "playing") {
                        pageRef.current.classList.add("isFullSize");
                    }
                    if (args === "paused") {
                        pageRef.current.classList.remove("isFullSize");
                    }
                }
                break;
            default:
        }
    };

    const onVideoClick = () => {
        if (!isVideoStarted) {
            setIsVideoStarted(true);
        }
    };

    const breadcrumbParent = [
        {
            title: emission?.nom || title,
            url: emission ? path.fill(path.EMISSION, emission) : "",
        },
    ];

    const episodeMedia: Media = {
        videoId: episode.videoId,
        referenceId: episode.id,
        legacyId: episode.mediaId,
    }
        

    const contentDataLayer = {
        item_id: episode?.id.toString(),
        item_variant: type,
        item_name: titre,
        item_categories: {
            item_category: emission?.nom,
        },
        propriete: emission?.nom || "Télé-Québec",
    };

    const { title: pageTitle, description: pageDescription } = getTitleDescription(
        title,
        description,
    );

    return (
        <Layout>
            <NextSeo
                description={pageDescription}
                nofollow={getSEONoFollow()}
                noindex={getSEONoIndex()}
                title={pageTitle}
            />

            <div className={classNames("page", styles.me)} ref={pageRef}>
                <Breadcrumb parentItem={breadcrumbParent} title={titre} />

                <div id="main-content" className={styles.content}>
                    <div className={styles.titlePubZone}>
                        <div>
                            {noSaison && noEpisode && (
                                <h4>{`Saison ${noSaison} - Épisode ${noEpisode}`}</h4>
                            )}
                            <h1 data-testid="episode-title">{titre}</h1>
                            <Date className={styles.dateDiffusion} dateTime={dateDiffusion} />
                        </div>
                    </div>

                    <div className={styles.header}>
                        {episodeMedia && (
                            <div className={styles.video} onClick={onVideoClick}>
                                <TqVideoPlayer media={episodeMedia} onEvent={handlePlayerEvent} />
                                {!isVideoStarted && <PlayIconInTo />}
                            </div>
                        )}

                        <div className={styles.metaright}>
                            {emission?.images &&
                                emission?.images.find(
                                    (x) => x.typeGabarit === TYPE_GABARIT.FORMAT_LOGO,
                                ) && (
                                    <Image
                                        alt={emission.titre}
                                        className={styles.logo}
                                        images={emission?.images}
                                        gabarits={[TYPE_GABARIT.FORMAT_LOGO]}
                                    />
                                )}
                            <div className={styles.desc}>
                                <p className={styles.descTxt}>{description}</p>
                                <BoutonsPartage
                                    className={styles.share}
                                    itemsToShow={["partager"]}
                                    media={mediaImage}
                                    contentDataLayer={contentDataLayer}
                                />
                            </div>
                        </div>
                    </div>

                    {recettes && recettes.length > 0 && (
                        <div className={styles.recettesListe}>
                            <h2>Recettes de l&apos;épisode</h2>
                            <Cartes contenus={recettes} />
                        </div>
                    )}

                    <VodDisponible />
                </div>
            </div>
        </Layout>
    );
};

export default EpisodePage;
